import React, { PropTypes } from 'react';
import ImmutablePropTypes from 'react-immutable-proptypes';
import BcReactComponent from '@bibliocommons/base-bc-react-component';
import FullScreenOverlay from '@bibliocommons/base-full-screen-overlay';
import BibBrief from '@bibliocommons/shared-bib-brief';
import { PrimaryButton, TextButton } from '@bibliocommons/base-buttons';
import JacketCover from '@bibliocommons/shared-jacket-cover';
import AccessibleTextArea from '@bibliocommons/base-accessibility-text-area';
import FormatInfo from '../../containers/FormatInfoContainer';
import { FormattedMessage } from 'react-intl';
import { AlertFailure } from '@bibliocommons/base-alerts';

import './GroupSearchFeedback.scss';

class GroupSearchFeedback extends BcReactComponent {
  constructor(props) {
    super(props);
    this.bindAll('handleShouldMatchChange', 'handleSelectMatchingBib', 'handleMessageChange', 'handleSubmit');
    this.state = {
      notes: ''
    };
  }

  componentDidUpdate(prevProps) {
    if (prevProps.isEditing !== this.props.isEditing && this.props.sourceBib && !this.hasMatches()) {
      this.props.groupSearchFeedbackActions.updateShouldMatch(true);
    }
  }

  handleShouldMatchChange(e) {
    const shouldMatch = e.currentTarget.value === 'true';
    this.props.groupSearchFeedbackActions.updateShouldMatch(shouldMatch);
  }

  handleSubmit(e) {
    e.preventDefault();
    if (!this.props.isSubmitting) {
      this.props.groupSearchFeedbackActions.submitFeedback({
        messagingId: this.props.sourceBib.get('id'),
        notes: this.state.notes
      });
    }
    this.setState({ notes: '' });
  }

  handleSelectMatchingBib(bib) {
    this.props.groupSearchFeedbackActions.selectMatchingBib(bib);
  }

  handleMessageChange(notes) {
    this.setState({ notes });
  }

  hasMatches() {
    const { sourceBib, searchResults } = this.props;
    return searchResults.find((group) => group.manifestations.includes(sourceBib)).size > 1;
  }

  renderMatchingBibSelect() {
    if (this.props.shouldMatch) {
      if (this.props.bibToMatch) {
        return (
          <div>
            <h4>It should be grouped with...</h4>
            <div className="matching-bib-list">
              <div className="search-result-bib">
                <div className="bib-jacket">
                  <JacketCover src={this.props.bibToMatch.getIn(['briefInfo', 'jacket'])} format={this.props.bibToMatch.getIn(['briefInfo', 'format'])} />
                </div>
                <div className="group-bib-brief-info">
                  <BibBrief bib={this.props.bibToMatch.get('briefInfo')} />
                  <FormatInfo item={this.props.bibToMatch} />
                  <TextButton className="change-matching-bib" handleClick={this.props.groupSearchFeedbackActions.changeMatchingBib}>
                    Pick a different item to group with
                  </TextButton>
                </div>
              </div>
            </div>
          </div>
        );
      }

      const items = this.props.searchResults
        .filter((group) => !group.manifestations.includes(this.props.sourceBib))
        .map((group) => group.representative);
      return (
        <div className="select-matching-bib">
          <h4>Select the item that this should be grouped with</h4>
          <div className="matching-bib-list">
            {items.map((item) => (
              <div className="search-result-bib">
                <div className="bib-jacket">
                  <JacketCover src={item.getIn(['briefInfo', 'jacket'])} format={item.getIn(['briefInfo', 'format'])} />
                </div>
                <div className="group-bib-brief-info">
                  <BibBrief bib={item.get('briefInfo')} />
                  <FormatInfo item={item} />
                  <PrimaryButton handleClick={() => this.handleSelectMatchingBib(item)} isLoading={this.props.isSubmitting}>
                    Group with this item
                  </PrimaryButton>
                </div>
              </div>
            ))}
          </div>
        </div>
      );
    }

    return null;
  }

  renderShouldMatchOptions() {
    if (this.hasMatches()) {
      return (
        <fieldset className="should-match">
          <legend className="should-match-label">This item should be...</legend>
          <div className="radio">
            <label htmlFor="diff-group">
              <input type="radio" name="shouldMatch" value="true" id="diff-group" onChange={this.handleShouldMatchChange} required />
              in a different group
            </label>
          </div>

          <div className="radio">
            <label htmlFor="no-group">
              <input type="radio" name="shouldMatch" value="false" id="no-group" onChange={this.handleShouldMatchChange} required />
              not be grouped
            </label>
          </div>
        </fieldset>
      );
    }

    return null;
  }

  renderOverlayContents() {
    const { sourceBib, isMissingRequiredFields } = this.props;

    if (!sourceBib) {
      return <noop />;
    }

    const sourceBibBrief = sourceBib.get('briefInfo');

    return (
      <div className="cp-group-search-feedback">
        <h2>
          Report incorrect grouping for...
        </h2>

        <div className="clearfix source-bib-display">
          <div className="source-bib-jacket-cover">
            <JacketCover src={sourceBibBrief.get('jacket')} format={sourceBibBrief.get('format')} alt={sourceBibBrief.get('title')} />
          </div>
          <div className="source-bib-info">
            <BibBrief bib={sourceBibBrief} titleTag="h3" />
            <FormatInfo item={sourceBib} />
          </div>
        </div>

        <form onSubmit={this.handleSubmit}>
          {this.renderShouldMatchOptions()}
          {this.renderMatchingBibSelect()}
          { isMissingRequiredFields ? <AlertFailure> <FormattedMessage id="groupSearchFeedbackFormError" /> </AlertFailure> : null}
          <label htmlFor="additional-comments" id="additional-comments-label">
            Additional comments
          </label>
          <AccessibleTextArea
            maxLength={4000}
            id="additional-comments"
            labelledById="additional-comments-label"
            value={this.state.notes}
            handleChange={this.handleMessageChange}
            errorKey="additional-comments"
            rows="5"
            cols="60" />

          <PrimaryButton type="submit" isLoading={this.props.isSubmitting}>
            Submit
          </PrimaryButton>
        </form>
      </div>
    );
  }

  render() {
    const { isEditing, groupSearchFeedbackActions: { closeOverlay } } = this.props;
    return (
      <FullScreenOverlay open={isEditing} handleClose={closeOverlay}>
        {this.renderOverlayContents()}
      </FullScreenOverlay>
    );
  }
}

GroupSearchFeedback.propTypes = {
  groupSearchFeedbackActions: PropTypes.objectOf(PropTypes.func),
  sourceBib: ImmutablePropTypes.map,
  bibToMatch: ImmutablePropTypes.map,
  shouldMatch: PropTypes.bool,
  isEditing: PropTypes.bool,
  isSubmitting: PropTypes.bool,
  searchResults: ImmutablePropTypes.listOf(
    ImmutablePropTypes.listOf(ImmutablePropTypes.map)
  )
};

export default GroupSearchFeedback;



// WEBPACK FOOTER //
// ./app/components/GroupSearchFeedback/GroupSearchFeedback.jsx